"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { Button } from "@/components/ui/button"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  // Initialize month based on selected date (from props) or current date
  const [month, setMonth] = React.useState<Date>(() => {
    // @ts-expect-error - selected is mode-specific
    const selectedDate = props.selected
    if (selectedDate && selectedDate instanceof Date) {
      return selectedDate
    }
    return new Date()
  })

  // Update month when selected changes (for controlled usage)
  React.useEffect(() => {
    // @ts-expect-error - selected is mode-specific
    const selectedDate = props.selected
    if (selectedDate && selectedDate instanceof Date) {
      setMonth(selectedDate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(props as { selected?: Date | { from?: Date; to?: Date } }).selected])

  // Sync internal month with prop month if provided (for controlled navigation)
  React.useEffect(() => {
    if (props.month) {
      setMonth(props.month)
    }
  }, [props.month])

  const handlePreviousYear = (e: React.MouseEvent) => {
    e.preventDefault()
    const newMonth = new Date(month.getFullYear() - 1, month.getMonth())
    setMonth(newMonth)
    props.onMonthChange?.(newMonth)
  }

  const handleNextYear = (e: React.MouseEvent) => {
    e.preventDefault()
    const newMonth = new Date(month.getFullYear() + 1, month.getMonth())
    setMonth(newMonth)
    props.onMonthChange?.(newMonth)
  }

  const handlePreviousMonth = (e: React.MouseEvent) => {
    e.preventDefault()
    const newMonth = new Date(month.getFullYear(), month.getMonth() - 1)
    setMonth(newMonth)
    props.onMonthChange?.(newMonth)
  }

  const handleNextMonth = (e: React.MouseEvent) => {
    e.preventDefault()
    const newMonth = new Date(month.getFullYear(), month.getMonth() + 1)
    setMonth(newMonth)
    props.onMonthChange?.(newMonth)
  }

  return (
    <div className="relative">
      {/* Custom navigation bar with 4 buttons */}
      <div className="flex justify-between items-center px-3 pt-3 pb-2">
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
            onClick={handlePreviousYear}
            type="button"
            aria-label="Go to previous year"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
            onClick={handlePreviousMonth}
            type="button"
            aria-label="Go to previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>

        <div className="text-sm font-medium">
          {month.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })}
        </div>

        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
            onClick={handleNextMonth}
            type="button"
            aria-label="Go to next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
            onClick={handleNextYear}
            type="button"
            aria-label="Go to next year"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* DayPicker without default navigation */}
      <DayPicker
        month={month}
        onMonthChange={setMonth}
        showOutsideDays={showOutsideDays}
        className={cn("p-3 pt-0", className)}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "hidden",
          caption_label: "text-sm font-medium",
          nav: "hidden",
          nav_button: "hidden",
          nav_button_previous: "hidden",
          nav_button_next: "hidden",
          table: "w-full border-collapse space-y-1",
          head_row: "!grid !grid-cols-7",
          head_cell:
            "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
          row: "!grid !grid-cols-7 w-full mt-2",
          cell: cn(
            "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 h-9 w-9 [&:has([aria-selected].day-range-end)]:rounded-r-md",
            props.mode === "range"
              ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md [&:has(>.day-range-middle)]:bg-accent"
              : "[&:has([aria-selected])]:rounded-md"
          ),
          day: cn(
            buttonVariants({ variant: "ghost" }),
            "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
          ),
          range_start: "day-range-start",
          range_end: "day-range-end",
          selected:
            "border-2 border-primary text-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
          today: "bg-accent text-accent-foreground",
          outside:
            "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
          disabled: "text-muted-foreground opacity-50",
          range_middle:
            "aria-selected:bg-accent aria-selected:text-accent-foreground bg-accent text-accent-foreground day-range-middle",
          hidden: "invisible",
          ...classNames,
        }}
        {...props}
      />
    </div>
  )
}
Calendar.displayName = "Calendar"

export { Calendar }

